"use client";

import { c as _c } from "react/compiler-runtime";
import { fallbackChainId, type ChainType } from "@/constants/chain-specific";
import ButtonThemed from "@/components/Buttons/ButtonThemed";
import VerticalSteps from "@/components/Buttons/VerticalSteps";
import BalanceInput from "@/components/Input/BalanceInput";
import Col from "@/components/Layout/Flex/Col";
import { Command } from "cmdk";
import { customFont } from "@/constants/project-specific";
import { stepperClasses } from "@/constants/project-specific/theme/stepcn";
import { Modal, cn, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/react";
import { Check, Link } from "lucide-react";
import TokenInput from "@/components/Input/TokenInput";
import { useState } from "react";
import type { TokenConfigType, TokenDataAllType, TokenDataType } from "@/constants/types/currency";
import useTokenData from "@/hooks/useTokenData";
import type { Address } from "viem";
import { useConnection } from "wagmi";
import { formatBalance } from "@/util/balance/formatBalance";
import { useAtom } from 'jotai';
import { recentTokensAtom, addToRecentTokens, getRecentTokensForChain, type RecentTokensStorage } from '@/config/atoms/recentTokens';
const Button = ButtonThemed;
interface Props {
  chain: ChainType;
  multichain?: boolean;
  onSelect?: (amount: string) => void;
  isOpen?: boolean | undefined;
  tokens: TokenConfigType[];
  onOpenChange?: (isOpen: boolean) => void;
  onChange?: (value: TokenConfigType) => void;
  account?: Address;
  label?: string;
  tokenData?: {
    [key: Address]: TokenDataAllType;
  };
}

// Add these pinned tokens - adjust according to your needs
const PINNED_TOKENS: TokenConfigType[] = [
  // Add 4-5 default important tokens for your platform
  // Example: WETH, USDC, DAI, etc.
];
export default function TokenSelect(t0) {
  const $ = _c(21);
  const {
    isOpen,
    onOpenChange,
    chain: t1,
    tokens,
    onChange,
    label,
    tokenData
  } = t0;
  const chain = t1 === undefined ? fallbackChainId : t1;
  useConnection();
  const [selectedToken, setSelectedToken] = useState();
  const [value, setValue] = useState("");
  const [recentTokensStorage, setRecentTokensStorage] = useAtom(recentTokensAtom);
  let t2;
  if ($[0] !== chain || $[1] !== recentTokensStorage) {
    t2 = getRecentTokensForChain(chain, recentTokensStorage);
    $[0] = chain;
    $[1] = recentTokensStorage;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const recentTokens = t2;
  let t3;
  if ($[3] !== chain || $[4] !== onChange || $[5] !== setRecentTokensStorage) {
    t3 = (token, onClose) => {
      setSelectedToken(token);
      setRecentTokensStorage(current => addToRecentTokens(token, chain, current));
      onChange?.(token);
      onClose();
    };
    $[3] = chain;
    $[4] = onChange;
    $[5] = setRecentTokensStorage;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleTokenSelect = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("cards-modal", customFont.className);
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== chain || $[9] !== handleTokenSelect || $[10] !== label || $[11] !== recentTokens || $[12] !== selectedToken?.address || $[13] !== tokenData || $[14] !== tokens || $[15] !== value) {
    t5 = <ModalContent>{onClose_0 => <><ModalHeader className="flex flex-col gap-1">{label ? label : "Select Token"}</ModalHeader><ModalBody><Command label="LFG"><Command.Input hidden={true} value={value} placeholder="Search token..." /><TokenInput onValueChange={setValue} value={value} onTokenChange={token_0 => handleTokenSelect(token_0, onClose_0)} chain={chain} /><Command.List><Command.Empty>No token found.</Command.Empty><Command.Group className="px-1 py-2 place-items-center w-full"><div className="flex flex-row flex-wrap gap-2 place-content-evenly">{(recentTokens.length > 0 ? recentTokens : PINNED_TOKENS).map(token_1 => <button key={token_1.address} type="button" onClick={() => handleTokenSelect(token_1, onClose_0)} className={cn("inline-flex items-center gap-1 px-2 py-1 rounded-full", "bg-gray-800 hover:bg-gray-700 transition-colors", "text-sm border border-gray-700")}><img src={token_1.logo} alt={token_1.symbol} className="w-4 h-4 object-contain" /><span>{token_1.symbol}</span></button>)}</div></Command.Group><Command.Group heading="All Tokens">{tokens?.map(token_2 => <Command.Item className={cn(selectedToken?.address === token_2.address ? "px-3 -mx-3 -my-2 pt-2 mt-2 -mb-2 pb-2 rounded-lg bg-black/15 shadow-sm shadow-white/5" : "pt-4", "cursor-pointer")} key={token_2.address} value={JSON.stringify(token_2)} keywords={[token_2.symbol, token_2.name, token_2.address]} onSelect={() => handleTokenSelect(token_2, onClose_0)}><div className="flex flex-row"><div className={cn("inline-flex gap-2 w-full")}><img src={token_2.logo} alt={token_2.symbol} className="object-contain" width="20px" height="20px" /><span className="text-medium">{token_2.symbol} - {token_2.name}</span></div><div className="flex flex-grow self-center text-right text-x h-full w-fit align-middle text-slate-500 text-sm">{tokenData?.[token_2.address as Address]?.balance?.formatted ? formatBalance(tokenData?.[token_2.address as Address]?.balance?.formatted ?? 0) : ""}</div></div></Command.Item>)}</Command.Group></Command.List></Command></ModalBody><ModalFooter>.</ModalFooter></>}</ModalContent>;
    $[8] = chain;
    $[9] = handleTokenSelect;
    $[10] = label;
    $[11] = recentTokens;
    $[12] = selectedToken?.address;
    $[13] = tokenData;
    $[14] = tokens;
    $[15] = value;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== isOpen || $[18] !== onOpenChange || $[19] !== t5) {
    t6 = <Modal isOpen={isOpen} onOpenChange={onOpenChange} backdrop="blur" placement="center" scrollBehavior="inside" className={t4}>{t5}</Modal>;
    $[17] = isOpen;
    $[18] = onOpenChange;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}